import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GoogleAdsRequestQuoteLink } from '@/components/GoogleAdsRequestQuoteLink';
import { 
  ArrowRight, 
  Shield, 
  Headphones, 
  Award, 
  GraduationCap,
  Sun,
  Zap,
  Wrench,
  BatteryCharging,
  Quote,
  Star,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { HoverGrid } from '../components/HoverGrid';
import { getGoogleReviews } from '../lib/googleReviewsService';

const trustPillars = [
  {
    icon: GraduationCap,
    title: 'Certified electrical expertise',
    description: 'Registered electricians and experienced PV installers guide every design and installation.'
  },
  {
    icon: Shield,
    title: 'Workmanship guaranteed',
    description: 'Professional installation standards backed by a one-year workmanship guarantee.'
  },
  {
    icon: Award,
    title: 'Premium supplier network',
    description: 'Carefully selected equipment from established brands, specified to suit each system.'
  },
  {
    icon: Headphones,
    title: 'Support beyond handover',
    description: 'After-sales service and maintenance help protect system performance over time.'
  }
];

const serviceCards = [
  {
    icon: Sun,
    title: 'Sustainable energy generation',
    description: 'PV systems designed around daily use, site conditions, and long-term value.',
    href: '/services'
  },
  {
    icon: BatteryCharging,
    title: 'Intelligent energy storage',
    description: 'Battery and backup capacity integrated into the wider system strategy.',
    href: '/services'
  },
  {
    icon: Zap,
    title: 'Reliable electrical infrastructure',
    description: 'Certified electrical work that supports safe, dependable energy performance.',
    href: '/services'
  },
  {
    icon: Wrench,
    title: 'Protecting your investment',
    description: 'Maintenance and support to keep your system working confidently over time.',
    href: '/services'
  }
];


const systemElements = [
  'Sustainable generation',
  'Intelligent storage',
  'Backup resilience',
  'Electrical infrastructure',
  'Maintenance and support'
];

const customerGroups = [
  'Homes and estates',
  'Farms and agricultural operations',
  'Commercial properties',
  'Growing businesses'
];

const processSteps = [
  'Understand your goals',
  'Assess current energy use',
  'Design the right system',
  'Install and commission',
  'Support your investment'
];

const partnerLogos = [
  { name: 'Fox ESS', initial: 'FE', logo: '/assets/supplier_logos/foxx-ess-logo.jpg' },
  { name: 'Sunsynk', initial: 'SS', logo: '/assets/supplier_logos/sunsynk-logo.webp' },
  { name: 'JA Solar', initial: 'JA', logo: '/assets/supplier_logos/ja-solar-logo.webp' },
  { name: 'Freedom Won', initial: 'FW', logo: '/assets/supplier_logos/freedom-won-logo.webp' },
  { name: 'MSPD Africa', initial: 'MP', logo: '/assets/supplier_logos/mspd-logo.png' },
  { name: 'Greenrich', initial: 'GR', logo: '/assets/supplier_logos/greenrich-logo.jpg' },
  { name: 'Revov', initial: 'RV', logo: '/assets/supplier_logos/revov-logo.jpg' },
];

export const Home = () => {
  const [googleTestimonials, setGoogleTestimonials] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const beastSectionRef = useRef(null);
  const [beastInView, setBeastInView] = useState(false);

  const effectiveTestimonials = googleTestimonials;
  const beastImage = '/assets/sep_promo.jpg';

  useEffect(() => {
    let active = true;
    let idleId;
    let timeoutId;
    const loadGoogleReviews = async () => {
      try {
        const reviews = await getGoogleReviews();
        if (!active) return;
        setGoogleTestimonials(reviews);
      } catch (error) {
        console.warn('Google reviews unavailable. Using fallback testimonials.', error.message);
      } finally {
        if (active) setGoogleLoading(false);
      }
    };

    timeoutId = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(loadGoogleReviews, { timeout: 3000 });
      } else {
        loadGoogleReviews();
      }
    }, 7000);

    return () => {
      active = false;
      if (idleId) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!effectiveTestimonials?.length) return;
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) =>
        prev === effectiveTestimonials.length - 1 ? 0 : prev + 1
      );
    }, 8000);
    return () => clearInterval(timer);
  }, [effectiveTestimonials]);

  useEffect(() => {
    setCurrentTestimonial(0);
  }, [effectiveTestimonials]);

  useEffect(() => {
    const node = beastSectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setBeastInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    if (!effectiveTestimonials?.length) return;
    setCurrentTestimonial((prev) => 
      prev === effectiveTestimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    if (!effectiveTestimonials?.length) return;
    setCurrentTestimonial((prev) => 
      prev === 0 ? effectiveTestimonials.length - 1 : prev - 1
    );
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <HoverGrid>
        <section 
          className="relative min-h-screen flex items-center bg-slate-950 overflow-hidden"
          data-testid="hero-section"
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/assets/home-solar-hero.webp)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/70 to-slate-950" />
          </div>

          {/* Green glow effect */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 pt-32 pb-20">
            <div className="max-w-3xl">
              <p className="text-emerald-400 text-sm font-medium uppercase tracking-widest mb-6 animate-fade-in">
                Intelligent energy systems for homes, farms and businesses
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up">
                Sustainable energy systems
                <span className="block text-emerald-400">designed around</span>
                the way you live and work
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl animate-fade-in-up delay-100">
                Green Light City designs reliable, intelligent energy systems that create greater independence, sustainability, operational confidence and long-term value.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-200">
                <Button 
                  asChild
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="hero-cta-quote"
                >
                  <GoogleAdsRequestQuoteLink to="/contact">
                    Book a System Assessment
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </GoogleAdsRequestQuoteLink>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  size="lg"
                  className="bg-transparent border-slate-600 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="hero-cta-solutions"
                >
                  <Link to="/services">
                    Explore Our Solutions
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-slate-500 rounded-full flex justify-center pt-2">
              <div className="w-1.5 h-3 bg-emerald-400 rounded-full" />
            </div>
          </div>
        </section>
      </HoverGrid>


      {/* Problem / Opportunity Section */}
      <section className="relative z-20 bg-white py-24" data-testid="energy-opportunity-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-emerald-600">A better energy future</p>
            <h2 className="text-3xl font-bold leading-tight text-slate-900 md:text-4xl">
              Your energy system should work for you every single day.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Rising costs, growing energy demands and sustainability goals make energy a long-term investment decision. The right system gives you greater control over how power is produced, stored and used.
            </p>
          </div>
        </div>
      </section>

      {/* Integrated Solution Section */}
      <section className="relative z-20 bg-slate-50 py-24" data-testid="integrated-solution-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-emerald-600">One intelligent system</p>
              <h2 className="text-3xl font-bold leading-tight text-slate-900 md:text-4xl">
                Designed as connected infrastructure, not separate products.
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                Solar, battery storage, backup capacity, electrical work and maintenance are planned together around your property, usage patterns and future needs.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {systemElements.map((element) => (
                <div key={element} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <p className="font-semibold text-slate-900">{element}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help + Trust Section */}
      <section className="relative z-20 bg-white py-24" data-testid="who-we-help-trust-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-emerald-600">Who we help</p>
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Energy confidence for real properties and operations.</h2>
              <div className="mt-8 grid gap-3">
                {customerGroups.map((group) => (
                  <div key={group} className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4 font-medium text-slate-800">
                    {group}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-emerald-600">Why Green Light City</p>
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">A partner who remains responsible after installation.</h2>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {trustPillars.map((usp, index) => (
                  <div key={usp.title} className="animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
                      <usp.icon className="h-7 w-7 text-emerald-600" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-slate-900">{usp.title}</h3>
                    <p className="text-sm leading-6 text-slate-600">{usp.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="relative z-20 bg-slate-950 py-24" data-testid="process-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-emerald-400">How we work</p>
            <h2 className="text-3xl font-bold text-white md:text-4xl">A calm, consultative path from assessment to long-term support.</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-5">
            {processSteps.map((step, index) => (
              <div key={step} className="rounded-3xl border border-slate-800 bg-slate-900 p-6">
                <p className="mb-6 text-sm font-semibold text-emerald-300">0{index + 1}</p>
                <h3 className="text-lg font-semibold text-white">{step}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative z-20 bg-slate-50 py-24" data-testid="services-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-16 text-center">
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-emerald-600">Solutions</p>
            <h2 className="mb-4 text-3xl font-bold text-slate-900 md:text-4xl">Services framed around outcomes.</h2>
            <p className="mx-auto max-w-2xl text-slate-600">
              Each service supports one goal: a more sustainable, independent and reliable energy system.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {serviceCards.map((service, index) => (
              <Card key={service.title} className="group relative overflow-hidden rounded-3xl border-slate-100 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`service-card-${index}`}>
                <CardContent className="p-7">
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 transition-colors group-hover:bg-emerald-600">
                    <service.icon className="h-7 w-7 text-emerald-600 transition-colors group-hover:text-white" />
                  </div>
                  <h3 className="mb-3 text-xl font-semibold text-slate-900">{service.title}</h3>
                  <p className="text-sm leading-6 text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Planning Tools */}
      <section className="relative z-20 bg-white py-20" data-testid="planning-tools-section">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid gap-6 rounded-[2rem] border border-slate-100 bg-slate-50 p-7 md:grid-cols-[1fr_auto] md:items-center md:p-10">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-emerald-600">Planning tools</p>
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Explore how intelligent energy systems work.</h2>
              <p className="mt-4 text-slate-600 md:text-lg">
                Use our Build-A-System planner and battery education guides as helpful starting points before a professional assessment.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
              <Button asChild size="lg" className="rounded-full bg-emerald-600 px-7 text-white hover:bg-emerald-700">
                <Link to="/inverter-game">Open Build-A-System</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full border-slate-300 bg-white px-7">
                <Link to="/how-solar-batteries-work">See How Systems Work</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* THE BEAST Feature */}
      <section
        ref={beastSectionRef}
        className="sticky top-0 z-0 flex min-h-screen items-center overflow-hidden bg-slate-950 pt-[50px] pb-[50px]"
        data-testid="beast-feature-section"
      >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(134,198,67,0.22),transparent_45%),linear-gradient(135deg,#020617_0%,#0f172a_45%,#111827_100%)]" />
          <div className="absolute -top-20 right-8 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-10 h-52 w-52 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="container relative z-10 mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[1fr_0.9fr] md:px-6">
            <div className={`max-w-2xl beast-reveal-left ${beastInView ? 'is-visible' : ''}`}>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.24em] text-emerald-300">
                AgriFox by Green Light City
              </p>
              <h2 className="mb-4 text-3xl font-bold text-white md:text-5xl">
                Commercial solar for farms and agriculture
              </h2>
              <p className="mb-8 text-base text-slate-300 md:text-lg">
                AgriFox designs solar and battery systems around the real energy demands of farms — from irrigation and boreholes to packhouses, workshops and essential operations.
              </p>
              <Button
                asChild
                size="lg"
                className="rounded-full bg-white px-8 text-slate-900 hover:bg-slate-100 focus-visible:ring-emerald-300"
              >
                <Link to="/agrifox">
                  Explore AgriFox
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className={`relative flex min-h-[320px] items-end justify-center md:min-h-[420px] beast-reveal-right ${beastInView ? 'is-visible' : ''}`}>
              <div className="pointer-events-none absolute bottom-10 h-14 w-56 rounded-[100%] bg-black/45 blur-xl" />
              <div className="pointer-events-none absolute bottom-24 h-32 w-32 rounded-full bg-emerald-300/20 blur-3xl" />
              <img
                src={beastImage}
                alt="AgriFox solar energy for farming"
                width={1080}
                height={1350}
                loading="lazy"
                className="beast-floating-image relative z-10 max-h-[560px] w-full max-w-[420px] rounded-3xl object-cover drop-shadow-[0_36px_38px_rgba(0,0,0,0.65)] transition-transform duration-500 ease-out hover:scale-[1.02] hover:-translate-y-2"
              />
            </div>
          </div>
      </section>

      {/* Partners Section */}
      <section className="relative z-20 py-16 bg-white border-y border-slate-100" data-testid="partners-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <p className="text-center text-sm font-medium text-slate-500 mb-10 uppercase tracking-wider">
            Premium suppliers selected for reliable system design
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
            {partnerLogos.map((partner) => (
              <div 
                key={partner.name}
                className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 hover:border-emerald-300 hover:shadow-lg transition-all cursor-default overflow-hidden"
                title={partner.name}
              >
                {partner.logo ? (
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-w-[80%] max-h-[80%] object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-xl font-bold text-slate-400">{partner.initial}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <HoverGrid>
        <section className="relative z-20 py-24 bg-slate-950" data-testid="testimonials-section">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-emerald-400 text-sm font-medium uppercase tracking-widest mb-4">
              Customer Stories
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
          </div>

          {!googleLoading && effectiveTestimonials.length > 0 && (
            <div className="max-w-4xl mx-auto relative">
              <div className="bg-slate-900 rounded-3xl p-8 md:p-12 border border-slate-800">
                <Quote className="w-12 h-12 text-emerald-500/30 mb-6" />
                <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-8">
                  "{effectiveTestimonials[currentTestimonial]?.content}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold text-lg">
                      {effectiveTestimonials[currentTestimonial]?.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {effectiveTestimonials[currentTestimonial]?.location && (
                        <span className="text-slate-400 text-sm">
                          {effectiveTestimonials[currentTestimonial]?.location}
                        </span>
                      )}
                      <span className="text-slate-600">•</span>
                      <span className="text-slate-400 text-sm">
                        {effectiveTestimonials[currentTestimonial]?.date}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i}
                        className={`w-5 h-5 ${
                          i < (effectiveTestimonials[currentTestimonial]?.rating || 5)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              {effectiveTestimonials.length > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8">
                  <button
                    onClick={prevTestimonial}
                    className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors border border-slate-800"
                    data-testid="testimonial-prev"
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <div className="flex gap-2">
                    {effectiveTestimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${
                          index === currentTestimonial
                            ? 'bg-emerald-500'
                            : 'bg-slate-700 hover:bg-slate-600'
                        }`}
                        aria-label={`Go to testimonial ${index + 1}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={nextTestimonial}
                    className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors border border-slate-800"
                    data-testid="testimonial-next"
                    aria-label="Next testimonial"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>
              )}
            </div>
          )}
          </div>
        </section>
      </HoverGrid>

      {/* CTA Section */}
      <section className="relative z-20 py-24 bg-emerald-600 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Let’s design an energy system that works for you.
          </h2>
          <p className="text-emerald-100 text-lg mb-10 max-w-2xl mx-auto">
            Invest in a system designed around your needs today and tomorrow, with professional support long after installation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-white text-emerald-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="cta-quote-btn"
            >
              <GoogleAdsRequestQuoteLink to="/contact">
                Book a System Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </GoogleAdsRequestQuoteLink>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="bg-transparent border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="cta-call-btn"
            >
              <GoogleAdsRequestQuoteLink href="tel:0686801986">
                Speak to an Energy Specialist
              </GoogleAdsRequestQuoteLink>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
